import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  TrendingUp, FileText, CheckCircle, ArrowRight, MessageCircle,
  Loader2, Smartphone, BarChart3, Play,
  Shield, Target, DollarSign, Building2,
  Star, Menu, X, AlertTriangle, Navigation, Zap,
  Clock, Eye, Wallet, PieChart
} from 'lucide-react';
import { toast } from 'sonner';
import { axiosInstance } from '../App';

const LandingPage = ({ setUser }) => {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ name: '', email: '', password: '' });
  const [forgotData, setForgotData] = useState({ email: '', whatsapp: '' });
  const [forgotResult, setForgotResult] = useState(null);
  const [forgotStep, setForgotStep] = useState('form');
  const [loading, setLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [videoConfig, setVideoConfig] = useState({
    video_enabled: false,
    video_url: '',
    video_title: '',
    video_description: ''
  });

  useEffect(() => {
    const fetchVideoConfig = async () => {
      try {
        const response = await axiosInstance.get('/landing-config');
        setVideoConfig(response.data);
      } catch (error) {
        console.error('Erro ao buscar config do vídeo:', error);
      }
    };
    fetchVideoConfig();
  }, []);

  // ========== AUTH HANDLERS ==========
  const _storeAuth = (data) => {
    const u = data.user || {};
    const user = {
      id: u.id || u.user_id,
      user_id: u.user_id || u.id,
      name: u.name,
      email: u.email,
      role: u.role,
      company_id: u.company_id,
      permissoes: u.permissoes || {},
      categoria: u.categoria || null,
    };
    if (data.access_token) localStorage.setItem('lucroliquido_access_token', data.access_token);
    if (data.refresh_token) localStorage.setItem('lucroliquido_refresh_token', data.refresh_token);
    localStorage.setItem('user', JSON.stringify(user));
    setUser(user);
    return user;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axiosInstance.post('/auth/login', loginData);
      if (response.data.access_token) {
        _storeAuth(response.data);
        toast.success('Login realizado com sucesso!');
        setShowLoginModal(false);
      } else {
        toast.error('Resposta inesperada do servidor.');
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erro ao fazer login. Verifique suas credenciais.';
      toast.error(typeof msg === 'string' ? msg : 'Credenciais inválidas');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axiosInstance.post('/auth/register', registerData);
      if (response.data.access_token) {
        _storeAuth(response.data);
        toast.success('Conta criada com sucesso! Bem-vindo ao Lucro Líquido!');
        setShowRegisterModal(false);
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erro ao criar conta. Este email pode já estar cadastrado.';
      toast.error(typeof msg === 'string' ? msg : 'Erro ao registrar');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axiosInstance.post('/auth/forgot-password', {
        email: forgotData.email,
        whatsapp: forgotData.whatsapp
      });
      setForgotResult(response.data);
      setForgotStep('success');
      toast.success('Dados verificados! Envie o link para seu WhatsApp.');
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao verificar dados';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const openWhatsAppWithLink = () => {
    if (!forgotResult) return;
    const cleanNumber = forgotResult.whatsapp.replace(/\D/g, '');
    const phoneNumber = cleanNumber.startsWith('55') ? cleanNumber : `55${cleanNumber}`;
    const resetUrl = `${window.location.origin}/reset-password/${forgotResult.token}`;
    const message = encodeURIComponent(
      `🔐 Link para redefinir sua senha do Lucro Líquido:\n\n${resetUrl}\n\n⏰ Este link expira em 1 hora.\n👆 Clique no link acima para definir sua nova senha.`
    );
    window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
  };

  const resetForgotPassword = () => {
    setForgotData({ email: '', whatsapp: '' });
    setForgotResult(null);
    setForgotStep('form');
  };

  const handleWhatsApp = () => {
    window.open('https://wa.link/1ku82x', '_blank');
  };

  // ========== SUB-COMPONENTS ==========
  const PhoneMockup = ({ children, className = "" }) => (
    <div className={`relative mx-auto ${className}`}>
      <div className="relative border-[8px] md:border-[12px] border-slate-800 rounded-[2rem] md:rounded-[2.5rem] bg-slate-800 shadow-2xl w-[200px] h-[400px] md:w-[280px] md:h-[560px]">
        <div className="absolute top-2 left-1/2 -translate-x-1/2 w-16 md:w-20 h-4 md:h-5 bg-slate-900 rounded-full"></div>
        <div className="w-full h-full rounded-[1.5rem] md:rounded-[2rem] overflow-hidden bg-slate-900">
          {children}
        </div>
      </div>
    </div>
  );

  const PainCard = ({ emoji, title, description }) => (
    <div className="p-6 rounded-2xl bg-slate-800/60 border border-slate-700/60 hover:border-orange-500/40 transition-all">
      <div className="text-3xl mb-3">{emoji}</div>
      <h3 className="text-white font-bold text-base mb-2">{title}</h3>
      <p className="text-slate-400 text-sm leading-relaxed">{description}</p>
    </div>
  );

  const FeatureCard = ({ icon: Icon, title, description, highlight = false }) => (
    <div className={`p-6 rounded-2xl border transition-all group ${
      highlight
        ? 'bg-gradient-to-br from-orange-500 to-amber-500 border-orange-400'
        : 'bg-slate-800/60 border-slate-700/60 hover:border-orange-500/40'
    }`}>
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
        highlight ? 'bg-white/20' : 'bg-orange-500/15 border border-orange-500/30'
      }`}>
        <Icon className={`w-6 h-6 ${highlight ? 'text-white' : 'text-orange-400'}`} />
      </div>
      <h3 className="text-white font-bold text-base mb-2">{title}</h3>
      <p className={`text-sm leading-relaxed ${highlight ? 'text-orange-100' : 'text-slate-400'}`}>{description}</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-950">

      {/* ========== NAVBAR ========== */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/90 backdrop-blur-lg border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-5 h-5 md:w-6 md:h-6 text-white" />
              </div>
              <span className="text-xl md:text-2xl font-bold text-white">Lucro<span className="text-orange-400">Líquido</span></span>
            </div>

            <div className="hidden md:flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => setShowLoginModal(true)}
                className="text-slate-300 hover:text-white"
                data-testid="nav-login-btn"
              >
                Entrar
              </Button>
              <Button
                onClick={() => setShowRegisterModal(true)}
                className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white rounded-full px-6"
                data-testid="nav-register-btn"
              >
                Use Grátis por 30 Dias
              </Button>
            </div>

            <button className="md:hidden p-2 text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-slate-800">
              <div className="flex flex-col gap-3">
                <Button variant="ghost" onClick={() => { setShowLoginModal(true); setMobileMenuOpen(false); }} className="w-full justify-center text-white">
                  Entrar
                </Button>
                <Button onClick={() => { setShowRegisterModal(true); setMobileMenuOpen(false); }} className="w-full bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-full">
                  Use Grátis por 30 Dias
                </Button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* ========== HERO ========== */}
      <section className="relative pt-28 md:pt-36 pb-20 md:pb-28 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 overflow-hidden">
        <div className="absolute top-32 right-0 w-[600px] h-[600px] bg-orange-500/8 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-amber-500/6 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">

            {/* Texto */}
            <div>
              <div className="inline-flex items-center gap-2 bg-orange-500/15 border border-orange-500/25 rounded-full px-4 py-2 mb-6">
                <Zap className="w-4 h-4 text-orange-400" />
                <span className="text-orange-300 text-sm font-medium">Gestão financeira em tempo real</span>
              </div>

              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
                Você sabe quanto{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-300">
                  sua empresa<br />lucrou hoje?
                </span>
              </h1>

              <p className="text-base md:text-lg lg:text-xl text-slate-300 mb-8 max-w-xl leading-relaxed">
                O <strong className="text-white">Lucro Líquido</strong> entrega o resultado financeiro real da sua empresa — sem esperar o fechamento do mês, sem depender do contador, direto no seu celular.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Button
                  onClick={() => setShowRegisterModal(true)}
                  className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg shadow-orange-500/25 transition-all hover:scale-105"
                  data-testid="hero-cta-btn"
                >
                  Quero ver meu lucro agora
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button
                  onClick={handleWhatsApp}
                  variant="outline"
                  className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white rounded-full px-8 py-6 text-lg"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Falar no WhatsApp
                </Button>
              </div>

              <div className="flex flex-wrap gap-5 text-sm text-slate-400">
                <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-orange-400" />30 dias grátis</span>
                <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-orange-400" />Sem cartão de crédito</span>
                <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-orange-400" />Cancele quando quiser</span>
              </div>
            </div>

            {/* Dashboard preview */}
            <div className="relative">
              <div className="bg-slate-800/50 backdrop-blur rounded-2xl border border-slate-700/50 p-5 shadow-2xl">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-3 h-3 rounded-full bg-red-500/70" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500/70" />
                  <div className="w-3 h-3 rounded-full bg-green-500/70" />
                  <span className="text-slate-500 text-xs ml-2">LucroLíquido — Painel Financeiro</span>
                </div>

                {/* GPS Bar */}
                <div className="mb-3 p-3 rounded-xl bg-gradient-to-r from-emerald-900/40 to-cyan-900/30 border border-emerald-500/30">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-emerald-400 font-medium flex items-center gap-1">
                      <Navigation className="w-3 h-3" /> GPS Financeiro · Break-even coberto
                    </span>
                    <span className="text-xs font-bold text-emerald-400">112%</span>
                  </div>
                  <div className="w-full h-2 bg-slate-700 rounded-full">
                    <div className="h-full w-[80%] bg-gradient-to-r from-emerald-500 to-cyan-400 rounded-full" />
                  </div>
                </div>

                {/* KPI row */}
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div className="bg-slate-700/40 rounded-lg p-3">
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Faturamento</p>
                    <p className="text-white font-bold text-base">R$ 87,4k</p>
                  </div>
                  <div className="bg-slate-700/40 rounded-lg p-3">
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Custos</p>
                    <p className="text-orange-400 font-bold text-base">R$ 52,1k</p>
                  </div>
                  <div className="bg-gradient-to-br from-purple-900/60 to-cyan-900/40 rounded-lg p-3 border border-purple-500/30">
                    <p className="text-[10px] text-purple-300 uppercase tracking-wider mb-1">Lucro Líq.</p>
                    <p className="text-cyan-400 font-bold text-base">+R$ 35,3k</p>
                  </div>
                </div>

                {/* Alerta */}
                <div className="p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/30 flex items-center gap-3">
                  <AlertTriangle className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-yellow-300 font-medium">2 contas vencem em 3 dias</p>
                    <p className="text-xs text-slate-400">R$ 4.800 a pagar · Fornecedor A, Aluguel</p>
                  </div>
                </div>
              </div>

              {/* Floating badge */}
              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl p-3 shadow-xl border border-slate-100 hidden sm:flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Margem líquida</p>
                  <p className="text-lg font-bold text-slate-900">40,4% esse mês</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== DORES ========== */}
      <section className="py-16 md:py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4">
              Quantas dessas situações você já viveu?
            </h2>
            <p className="text-slate-400 text-base md:text-lg max-w-2xl mx-auto">
              Se reconhecer em qualquer uma delas, o Lucro Líquido foi feito para você.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            <PainCard
              emoji="😰"
              title="Tem dinheiro na conta mas não sabe se está lucrando"
              description="Caixa cheio não é sinônimo de lucro. Você pode estar gastando mais do que entra sem perceber."
            />
            <PainCard
              emoji="📆"
              title="Só sabe o resultado no fechamento do mês com o contador"
              description="Decisões importantes não podem esperar 30 dias. Você precisa do resultado agora."
            />
            <PainCard
              emoji="🔥"
              title="Descobre que tem conta vencida quando o fornecedor cobra"
              description="Juros e multas evitáveis por falta de visibilidade do que está vencendo nos próximos dias."
            />
            <PainCard
              emoji="😓"
              title="A empresa trava quando você não está presente"
              description="Sua equipe não tem acesso à informação financeira e você precisa responder tudo na hora."
            />
          </div>
        </div>
      </section>

      {/* ========== O QUE É ========== */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-slate-950 to-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-orange-500/15 border border-orange-500/25 rounded-full px-4 py-2 mb-6">
                <Star className="w-4 h-4 text-orange-400" />
                <span className="text-orange-300 text-sm font-medium">Uma nova forma de gerir seu dinheiro</span>
              </div>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-6">
                Gestão financeira que qualquer empresário consegue usar —{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-300">
                  sem precisar de contador todo dia
                </span>
              </h2>
              <p className="text-slate-300 text-base md:text-lg mb-8 leading-relaxed">
                O Lucro Líquido centraliza entradas, saídas, contas a pagar e receber, e transforma tudo em <strong className="text-white">relatórios financeiros claros e automáticos</strong> — DRE, Fluxo de Caixa, GPS de break-even e alertas antes que o problema aconteça.
              </p>
              <ul className="space-y-4">
                {[
                  'Resultado do mês atualizado em tempo real, sem esperar fechamento',
                  'Alertas antes que a conta vença — não depois',
                  'Seu lucro real, não o lucro que você imagina ter',
                  'Acesso pelo celular para você e sua equipe, a qualquer hora',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-300">
                    <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Números rápidos */}
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: Clock, value: '5 min', label: 'para ter seu primeiro dashboard funcionando', color: 'orange' },
                { icon: Eye, value: '100%', label: 'de visibilidade do seu fluxo de caixa nos próximos 30 dias', color: 'cyan' },
                { icon: AlertTriangle, value: '0', label: 'surpresas com contas vencidas quando você usa os alertas', color: 'yellow' },
                { icon: Smartphone, value: '24/7', label: 'de acesso pelo celular — de qualquer lugar do mundo', color: 'emerald' },
              ].map(({ icon: Icon, value, label, color }, i) => (
                <div key={i} className={`p-6 rounded-2xl bg-${color}-500/8 border border-${color}-500/25`}>
                  <Icon className={`w-6 h-6 text-${color}-400 mb-3`} />
                  <p className={`text-3xl font-black text-${color}-400 mb-1`}>{value}</p>
                  <p className="text-slate-400 text-sm leading-snug">{label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ========== FUNCIONALIDADES ========== */}
      <section className="py-16 md:py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4">
              Tudo que você precisa para conhecer seu{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-300">lucro real</span>
            </h2>
            <p className="text-slate-400 text-base md:text-lg max-w-2xl mx-auto">
              Módulos integrados que trabalham juntos e se atualizam automaticamente a cada lançamento.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">

            {/* GPS Financeiro — card destaque */}
            <div className="md:col-span-2 p-6 md:p-8 rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 border border-slate-700/60 hover:border-orange-500/30 transition-all">
              <div className="flex flex-col md:flex-row gap-6 md:gap-8">
                <div className="flex-1">
                  <div className="w-12 h-12 bg-orange-500/15 border border-orange-500/30 rounded-xl flex items-center justify-center mb-4">
                    <Navigation className="w-6 h-6 text-orange-400" />
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-white mb-3">GPS Financeiro — Break-even em tempo real</h3>
                  <p className="text-slate-300 text-sm md:text-base mb-4 leading-relaxed">
                    O empresário precisa saber todo dia se o mês vai fechar no azul ou no vermelho. O GPS Financeiro calcula seu <strong className="text-orange-400">ponto de equilíbrio</strong> e mostra em tempo real o quanto você já cobriu dos seus custos de existência — e o quanto falta para começar a lucrar de verdade.
                  </p>
                  <ul className="space-y-2 text-sm text-slate-300">
                    <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-orange-400 flex-shrink-0" />Fórmula: Custos Fixos ÷ Margem de Contribuição</li>
                    <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-orange-400 flex-shrink-0" />Gráfico dia a dia da receita vs meta de break-even</li>
                    <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-orange-400 flex-shrink-0" />Quanto você precisa faturar por dia para empatar o mês</li>
                  </ul>
                </div>
                <div className="hidden md:block w-48 flex-shrink-0">
                  <div className="bg-slate-900 border border-slate-700 rounded-xl p-4 h-full">
                    <p className="text-xs text-slate-400 mb-3">Hoje, {new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}</p>
                    <div className="relative w-20 h-20 mx-auto mb-3">
                      <svg className="w-full h-full -rotate-90">
                        <circle cx="40" cy="40" r="32" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="6" />
                        <circle cx="40" cy="40" r="32" fill="none" stroke="#10B981" strokeWidth="6" strokeLinecap="round" strokeDasharray="140 201" />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-lg font-black text-emerald-400">70%</span>
                      </div>
                    </div>
                    <p className="text-xs text-center text-slate-400">Break-even coberto</p>
                    <div className="mt-3 pt-3 border-t border-slate-700 space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">Meta:</span>
                        <span className="text-white font-medium">R$ 32k</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">Realizado:</span>
                        <span className="text-emerald-400 font-medium">R$ 22,4k</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <FeatureCard
              icon={FileText}
              title="DRE Automática"
              description="Receita bruta, custos, despesas, lucro bruto e lucro líquido calculados automaticamente — com margem % em tempo real. Sem esperar o contador."
              highlight
            />

            <FeatureCard
              icon={BarChart3}
              title="Fluxo de Caixa Projetado"
              description="Veja seu saldo dia a dia pelos próximos 30 dias. Saiba com antecedência se o caixa vai ficar negativo e tome ação antes que aconteça."
            />

            <FeatureCard
              icon={AlertTriangle}
              title="Alertas de Risco"
              description="Contas vencendo nos próximos dias, margem caindo abaixo do saudável, caixa projetado negativo. Alertas antes do problema, não depois."
            />

            <FeatureCard
              icon={Target}
              title="Termômetro de Meta"
              description="Defina a meta de lucro do mês e acompanhe o progresso em uma barra visual. A equipe inteira sabe onde está e quanto falta."
            />

            <FeatureCard
              icon={Wallet}
              title="Contas a Pagar e Receber"
              description="Controle completo com status, vencimento, categorias e agendamento. Repetição mensal automática e upload de documentos anexados."
            />

            <FeatureCard
              icon={PieChart}
              title="14 Relatórios Financeiros"
              description="DFC, DRE gerencial, aging de contas, Pareto de despesas, ranking de clientes, comparativo mensal e muito mais — todos exportáveis."
            />
          </div>
        </div>
      </section>

      {/* ========== APP DO PROPRIETÁRIO ========== */}
      <section className="py-16 md:py-24 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="order-2 lg:order-1 flex justify-center">
              <PhoneMockup>
                <div className="h-full bg-gradient-to-b from-slate-800 to-slate-900 p-4 text-white">
                  <div className="text-center mb-4 pt-4">
                    <Building2 className="w-10 h-10 mx-auto mb-2 text-orange-400" />
                    <h4 className="font-bold text-base">App do Proprietário</h4>
                    <p className="text-xs text-slate-400">Acesso pelo celular · 24h</p>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div className="bg-orange-500/15 rounded-lg p-3 text-center border border-orange-500/20">
                      <p className="text-[10px] text-slate-400 mb-1">Faturamento</p>
                      <p className="text-base font-bold text-orange-400">R$ 87k</p>
                    </div>
                    <div className="bg-emerald-500/15 rounded-lg p-3 text-center border border-emerald-500/20">
                      <p className="text-[10px] text-slate-400 mb-1">Lucro</p>
                      <p className="text-base font-bold text-emerald-400">R$ 35k</p>
                    </div>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3 mb-2">
                    <p className="text-[10px] text-slate-400 mb-2">GPS Financeiro</p>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2.5 bg-slate-700 rounded-full">
                        <div className="w-[70%] h-full bg-gradient-to-r from-orange-500 to-amber-400 rounded-full" />
                      </div>
                      <span className="text-xs font-bold text-orange-400">70%</span>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <div className="bg-green-500/10 rounded-lg p-2.5 flex justify-between items-center border border-green-500/20">
                      <span className="text-xs text-slate-300">A Receber</span>
                      <span className="text-xs font-bold text-green-400">R$ 42k</span>
                    </div>
                    <div className="bg-red-500/10 rounded-lg p-2.5 flex justify-between items-center border border-red-500/20">
                      <span className="text-xs text-slate-300">A Pagar</span>
                      <span className="text-xs font-bold text-red-400">R$ 18k</span>
                    </div>
                    <div className="bg-yellow-500/10 rounded-lg p-2.5 flex items-center gap-2 border border-yellow-500/20">
                      <AlertTriangle className="w-3 h-3 text-yellow-400" />
                      <span className="text-xs text-yellow-300">2 contas vencem em 3 dias</span>
                    </div>
                  </div>
                </div>
              </PhoneMockup>
            </div>

            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 bg-slate-800 rounded-full px-4 py-2 mb-6 border border-slate-700">
                <Smartphone className="w-4 h-4 text-orange-400" />
                <span className="text-slate-300 text-sm font-medium">App do Proprietário</span>
              </div>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-6">
                Sua empresa na palma da mão —{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-300">
                  mesmo quando você está viajando
                </span>
              </h2>
              <p className="text-slate-300 mb-8 text-base md:text-lg leading-relaxed">
                O App do Proprietário é uma visão executiva da saúde financeira da empresa: faturamento, lucro, contas a pagar e receber, GPS Financeiro e alertas — tudo em uma tela, acessível pelo celular a qualquer hora.
              </p>
              <ul className="space-y-4">
                {[
                  { title: 'Acesso seguro via link personalizado', desc: 'Sem precisar instalar nada — funciona direto no navegador do celular' },
                  { title: 'Visão do dia: o que entra e o que sai', desc: 'Contas a vencer, saldo projetado e resultado do mês em uma tela só' },
                  { title: 'Alertas prioritários em destaque', desc: 'As situações que precisam da sua atenção aparecem primeiro, sem precisar procurar' },
                ].map(({ title, desc }, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-orange-500/20 border border-orange-500/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-3.5 h-3.5 text-orange-400" />
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">{title}</p>
                      <p className="text-slate-400 text-sm">{desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ========== COMO FUNCIONA ========== */}
      <section className="py-16 md:py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4">
              Simples de começar. Poderoso de usar.
            </h2>
            <p className="text-slate-400 text-base md:text-lg max-w-xl mx-auto">
              Em 3 passos você já tem um painel financeiro completo funcionando.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {[
              {
                step: '01',
                icon: Building2,
                title: 'Crie sua empresa',
                desc: 'Cadastre a empresa, configure o saldo inicial e ajuste as categorias ao seu negócio. Leva menos de 5 minutos.',
                color: 'orange',
              },
              {
                step: '02',
                icon: Wallet,
                title: 'Lance entradas e saídas',
                desc: 'Registre seus lançamentos financeiros, contas a pagar e a receber. O sistema categoriza e calcula tudo automaticamente.',
                color: 'amber',
              },
              {
                step: '03',
                icon: TrendingUp,
                title: 'Veja seu lucro real',
                desc: 'DRE, Fluxo de Caixa, GPS Financeiro e alertas — atualizados em tempo real. Seu resultado agora, não no mês que vem.',
                color: 'cyan',
              },
            ].map(({ step, icon: Icon, title, desc, color }, i) => (
              <div key={i} className={`relative p-6 md:p-8 rounded-2xl bg-slate-800/50 border border-slate-700/60`}>
                <div className={`text-5xl font-black text-${color}-500/20 mb-4 leading-none`}>{step}</div>
                <div className={`w-12 h-12 bg-${color}-500/15 border border-${color}-500/30 rounded-xl flex items-center justify-center mb-4`}>
                  <Icon className={`w-6 h-6 text-${color}-400`} />
                </div>
                <h3 className="text-white font-bold text-lg mb-2">{title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== VÍDEO (se habilitado pelo admin) ========== */}
      {videoConfig.video_enabled && videoConfig.video_url && (
        <section className="py-16 md:py-24 bg-slate-950">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8 md:mb-12">
              <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/20 rounded-full px-4 py-2 mb-4">
                <Play className="w-4 h-4 text-orange-400" />
                <span className="text-orange-300 text-sm font-medium">Apresentação</span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                {videoConfig.video_title || 'Veja como funciona'}
              </h2>
              {videoConfig.video_description && (
                <p className="text-slate-400 max-w-2xl mx-auto">{videoConfig.video_description}</p>
              )}
            </div>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-orange-500/10 border border-white/10">
              <div className="aspect-video bg-black">
                {(videoConfig.video_url.includes('youtube.com') || videoConfig.video_url.includes('youtu.be')) ? (
                  <iframe
                    className="w-full h-full"
                    src={`https://www.youtube.com/embed/${
                      videoConfig.video_url.includes('youtu.be')
                        ? videoConfig.video_url.split('youtu.be/')[1]?.split('?')[0]
                        : videoConfig.video_url.split('v=')[1]?.split('&')[0]
                    }?rel=0`}
                    title={videoConfig.video_title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : videoConfig.video_url.includes('vimeo.com') ? (
                  <iframe
                    className="w-full h-full"
                    src={`https://player.vimeo.com/video/${videoConfig.video_url.split('vimeo.com/')[1]?.split('?')[0]}`}
                    title={videoConfig.video_title}
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                  />
                ) : null}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ========== GARANTIAS / CONFIANÇA ========== */}
      <section className="py-12 md:py-16 bg-slate-900 border-y border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {[
              { icon: Shield, title: 'Dados protegidos', desc: 'Criptografia de ponta a ponta. Seus dados financeiros só são seus.' },
              { icon: Zap, title: 'Atualizações em tempo real', desc: 'Cada lançamento reflete imediatamente em todos os relatórios.' },
              { icon: Smartphone, title: 'Funciona no celular', desc: 'App do Proprietário acessível de qualquer dispositivo, sem instalar nada.' },
              { icon: Star, title: '30 dias sem custo', desc: 'Teste completo por 30 dias sem cartão de crédito. Se não resolver, não cobra nada.' },
            ].map(({ icon: Icon, title, desc }, i) => (
              <div key={i} className="text-center">
                <div className="w-12 h-12 bg-orange-500/15 rounded-xl flex items-center justify-center mx-auto mb-3 border border-orange-500/20">
                  <Icon className="w-6 h-6 text-orange-400" />
                </div>
                <h3 className="text-white font-bold text-sm mb-1">{title}</h3>
                <p className="text-slate-400 text-xs leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== CTA FINAL ========== */}
      <section className="py-20 md:py-28 bg-gradient-to-br from-orange-500 to-amber-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 mb-6">
            <TrendingUp className="w-4 h-4 text-white" />
            <span className="text-white text-sm font-medium">Use por 30 dias sem custo!</span>
          </div>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
            Pare de adivinhar.<br />Saiba seu lucro real.
          </h2>

          <p className="text-lg md:text-xl text-orange-100 mb-10 max-w-2xl mx-auto">
            Em 5 minutos você tem um painel financeiro completo. Em 30 dias você vai entender sua empresa de um jeito que nunca entendeu antes — sem custo algum.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button
              onClick={() => setShowRegisterModal(true)}
              className="bg-white text-orange-600 hover:bg-orange-50 rounded-full px-8 py-6 text-lg font-bold shadow-lg transition-all hover:scale-105"
              data-testid="final-cta-btn"
            >
              Começar Teste Grátis Agora
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              onClick={handleWhatsApp}
              variant="outline"
              className="border-white/60 text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              Tirar Dúvidas pelo WhatsApp
            </Button>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-orange-100">
            <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4" />30 dias grátis</span>
            <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4" />Sem cartão de crédito</span>
            <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4" />Suporte incluído</span>
            <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4" />Cancele quando quiser</span>
          </div>
        </div>
      </section>

      {/* ========== FOOTER ========== */}
      <footer className="py-8 md:py-12 bg-slate-950 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-amber-500 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-bold text-white">Lucro<span className="text-orange-400">Líquido</span></span>
            </div>
            <p className="text-slate-500 text-sm text-center">
              © 2025 LucroLíquido — Gestão financeira inteligente para empresas que querem conhecer seu lucro real.
            </p>
          </div>
        </div>
      </footer>

      {/* ========== MODAIS (lógica intocada) ========== */}

      {/* Login */}
      <Dialog open={showLoginModal} onOpenChange={setShowLoginModal}>
        <DialogContent className="sm:max-w-md bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-white">Entrar no Lucro Líquido</DialogTitle>
            <DialogDescription className="text-slate-400">
              Entre com suas credenciais para acessar o sistema
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="login-email" className="text-white">Email</Label>
              <Input id="login-email" type="email" placeholder="seu@email.com" value={loginData.email}
                onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                required className="bg-slate-800 border-slate-700 text-white" data-testid="login-email-input" />
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label htmlFor="login-password" className="text-white">Senha</Label>
                <button type="button" onClick={() => { setShowLoginModal(false); setShowForgotPasswordModal(true); setForgotData({ ...forgotData, email: loginData.email }); }}
                  className="text-xs text-orange-400 hover:text-orange-300" data-testid="forgot-password-link">
                  Esqueci minha senha
                </button>
              </div>
              <Input id="login-password" type="password" placeholder="••••••••" value={loginData.password}
                onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                required className="bg-slate-800 border-slate-700 text-white" data-testid="login-password-input" />
            </div>
            <Button type="submit" className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white"
              disabled={loading} data-testid="login-submit-btn">
              {loading ? 'Entrando...' : 'Entrar'}
            </Button>
            <div className="text-center text-sm text-slate-400">
              Não tem conta?{' '}
              <button type="button" onClick={() => { setShowLoginModal(false); setShowRegisterModal(true); }} className="text-orange-400 hover:text-orange-300">
                Cadastre-se grátis
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Esqueci a senha */}
      <Dialog open={showForgotPasswordModal} onOpenChange={(open) => { setShowForgotPasswordModal(open); if (!open) resetForgotPassword(); }}>
        <DialogContent className="sm:max-w-md bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-white">Recuperar Senha</DialogTitle>
            <DialogDescription className="text-slate-400">
              {forgotStep === 'form' ? 'Digite seu email e o WhatsApp cadastrado na empresa' : 'Link de recuperação gerado com sucesso!'}
            </DialogDescription>
          </DialogHeader>

          {forgotStep === 'form' && (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="forgot-email" className="text-white">Seu Email</Label>
                <Input id="forgot-email" type="email" placeholder="seu@email.com" value={forgotData.email}
                  onChange={(e) => setForgotData({ ...forgotData, email: e.target.value })}
                  required className="bg-slate-800 border-slate-700 text-white" data-testid="forgot-email-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="forgot-whatsapp" className="text-white">WhatsApp da Empresa</Label>
                <Input id="forgot-whatsapp" type="tel" placeholder="(11) 99999-9999" value={forgotData.whatsapp}
                  onChange={(e) => setForgotData({ ...forgotData, whatsapp: e.target.value })}
                  required className="bg-slate-800 border-slate-700 text-white" data-testid="forgot-whatsapp-input" />
                <p className="text-xs text-slate-500">Digite o WhatsApp cadastrado nos dados da empresa para verificação</p>
              </div>
              <Button type="submit" className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600"
                disabled={loading} data-testid="forgot-submit-btn">
                {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Verificando...</>) : 'Verificar e Gerar Link'}
              </Button>
              <div className="text-center">
                <button type="button" onClick={() => { setShowForgotPasswordModal(false); setShowLoginModal(true); }} className="text-sm text-slate-400 hover:text-white">
                  Voltar ao login
                </button>
              </div>
            </form>
          )}

          {forgotStep === 'success' && forgotResult && (
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-orange-500/10 border border-orange-500/30">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-orange-400" />
                  <span className="text-orange-400 font-semibold">Dados verificados!</span>
                </div>
                <p className="text-sm text-slate-300">
                  Olá <span className="text-white font-medium">{forgotResult.user_name}</span>, seu link de recuperação foi gerado.
                </p>
              </div>
              <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
                <p className="text-sm text-slate-300 mb-3">
                  Clique no botão abaixo para abrir o WhatsApp com o link de recuperação.
                  <span className="text-blue-300 block mt-1">Envie a mensagem para você mesmo ou para um contato de confiança.</span>
                </p>
                <p className="text-xs text-slate-500">⏰ O link expira em 1 hora</p>
              </div>
              <Button onClick={openWhatsAppWithLink} className="w-full bg-green-600 hover:bg-green-700" data-testid="send-whatsapp-btn">
                <MessageCircle className="w-4 h-4 mr-2" />Enviar Link para Meu WhatsApp
              </Button>
              <div className="text-center space-y-2">
                <button type="button" onClick={() => { setForgotStep('form'); setForgotResult(null); }} className="text-sm text-slate-400 hover:text-white">
                  Tentar com outro email
                </button>
                <br />
                <button type="button" onClick={() => { setShowForgotPasswordModal(false); setShowLoginModal(true); resetForgotPassword(); }} className="text-sm text-orange-400 hover:text-orange-300">
                  Voltar ao login
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Registro */}
      <Dialog open={showRegisterModal} onOpenChange={setShowRegisterModal}>
        <DialogContent className="sm:max-w-md bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-white">Comece seu teste grátis</DialogTitle>
            <DialogDescription className="text-slate-400">
              30 dias grátis · Sem cartão de crédito · Cancele quando quiser
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleRegister} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="register-name" className="text-white">Nome Completo</Label>
              <Input id="register-name" type="text" placeholder="Seu nome" value={registerData.name}
                onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                required className="bg-slate-800 border-slate-700 text-white" data-testid="register-name-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="register-email" className="text-white">Email</Label>
              <Input id="register-email" type="email" placeholder="seu@email.com" value={registerData.email}
                onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                required className="bg-slate-800 border-slate-700 text-white" data-testid="register-email-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="register-password" className="text-white">Senha</Label>
              <Input id="register-password" type="password" placeholder="Mínimo 6 caracteres" value={registerData.password}
                onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                required minLength={6} className="bg-slate-800 border-slate-700 text-white" data-testid="register-password-input" />
            </div>
            <Button type="submit" className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white"
              disabled={loading} data-testid="register-submit-btn">
              {loading ? 'Criando conta...' : 'Começar teste grátis agora'}
            </Button>
            <div className="text-center text-sm text-slate-400">
              Já tem conta?{' '}
              <button type="button" onClick={() => { setShowRegisterModal(false); setShowLoginModal(true); }} className="text-orange-400 hover:text-orange-300">
                Faça login
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default LandingPage;
